import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../../api/client';
import {
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
  BG_CARD,
  FONT_HEADING,
} from '../designTokens';

interface RunSummary {
  id: string;
  status: string;
  guestSelectionMode: string;
  totalProcessed: number;
  totalAssigned: number;
  totalUnplaced: number;
  rulesApplied: { id: string; name: string; ruleType: string }[];
  createdAt: string;
  completedAt: string | null;
  approvedAt: string | null;
  createdBy: { id: string; fullName: string } | null;
  _count: { entries: number };
}

interface RunDetail {
  id: string;
  status: string;
  totalProcessed: number;
  totalAssigned: number;
  totalUnplaced: number;
  warnings: string[];
  entries: {
    id: string;
    seatNumber: number;
    tableNumber: number;
    zoneName: string;
    ruleThatPlaced: string;
    groupLabel: string | null;
    status: string;
    guest: { id: string; fullName: string; ticketNumber: string | null; chapterAffiliation: string | null };
    table: { id: string; tableNumber: number; tableName: string | null; zone: { name: string; colorCode: string } | null };
  }[];
  createdBy: { id: string; fullName: string } | null;
  approvedBy: { id: string; fullName: string } | null;
}

const STATUS_BADGE_STYLES: Record<string, React.CSSProperties> = {
  Approved: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  Pending: { backgroundColor: '#eceff1', color: '#546e7a' },
  Completed: { backgroundColor: '#eceff1', color: '#546e7a' },
  PartiallyApproved: { backgroundColor: '#fff3e0', color: '#e65100' },
  Rejected: { backgroundColor: '#fdecea', color: '#d32f2f' },
};

const styles: Record<string, React.CSSProperties> = {
  container: {},
  title: {
    fontSize: '0.95rem',
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 1rem 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
  },
  th: {
    padding: '0.5rem 0.6rem',
    textAlign: 'left' as const,
    fontSize: '0.72rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
  },
  td: {
    padding: '0.55rem 0.6rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    verticalAlign: 'top' as const,
  },
  row: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: '0.68rem',
    fontWeight: 600,
    padding: '0.15rem 0.45rem',
    borderRadius: '3px',
  },
  expandedRow: {
    backgroundColor: BG_SURFACE,
  },
  detailPanel: {
    padding: '1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_SURFACE,
  },
  detailTableGroup: {
    marginBottom: '0.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: BG_CARD,
  },
  detailTableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.4rem 0.7rem',
    backgroundColor: BG_SURFACE,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.78rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  detailGuestRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.3rem 0.7rem',
    fontSize: '0.76rem',
    borderBottom: '1px solid #f5f5f5',
    gap: '0.5rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px dashed ${BORDER_LIGHT}`,
  },
  rulesList: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap' as const,
  },
  ruleChip: {
    fontSize: '0.65rem',
    padding: '0.1rem 0.3rem',
    borderRadius: '3px',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
};

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const timePart = dateStr.includes('T') ? dateStr.split('T')[1]?.slice(0, 5) : '';

  const [year, month, day] = datePart.split('-');
  let timeDisplay = '';
  if (timePart) {
    const [hours, minutes] = timePart.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    timeDisplay = ` ${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  return `${month}/${day}/${year}${timeDisplay}`;
}

export function RunHistoryTable() {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [runDetail, setRunDetail] = useState<RunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reversing, setReversing] = useState(false);

  const fetchRuns = useCallback(async () => {
    try {
      const data = await apiClient<RunSummary[]>('/batch-assignment/runs');
      setRuns(data);
      setError('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to load run history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const handleReverseRun = async (runId: string) => {
    const confirmed = window.confirm(
      'Reverse this run? All seats assigned by this batch will be unassigned.',
    );
    if (!confirmed) return;

    setReversing(true);
    try {
      const result = await apiClient<{ message: string; reversed: number; skipped: number }>(
        `/batch-assignment/runs/${runId}/reverse`,
        { method: 'POST' },
      );
      alert(result.message);
      setExpandedRunId(null);
      setRunDetail(null);
      fetchRuns();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to reverse run.');
    } finally {
      setReversing(false);
    }
  };

  const handleRowClick = async (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      setRunDetail(null);
      return;
    }

    setExpandedRunId(runId);
    setDetailLoading(true);
    try {
      const data = await apiClient<RunDetail>(`/batch-assignment/runs/${runId}`);
      setRunDetail(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to load run detail.');
      setExpandedRunId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading run history...</div>;
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Run History</h3>

      {error && <div style={styles.error} role="alert">{error}</div>}

      {runs.length === 0 && (
        <div style={styles.empty}>
          No batch assignment runs have been executed yet.
        </div>
      )}

      {runs.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date/Time</th>
              <th style={styles.th}>Initiated By</th>
              <th style={styles.th}>Rules</th>
              <th style={styles.th}>Processed</th>
              <th style={styles.th}>Assigned</th>
              <th style={styles.th}>Unplaced</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <React.Fragment key={run.id}>
                <tr
                  style={{
                    ...styles.row,
                    backgroundColor: expandedRunId === run.id ? BG_SURFACE : 'transparent',
                  }}
                  onClick={() => handleRowClick(run.id)}
                  role="button"
                  aria-expanded={expandedRunId === run.id}
                >
                  <td style={styles.td}>{formatDateTime(run.createdAt)}</td>
                  <td style={styles.td}>{run.createdBy?.fullName || '—'}</td>
                  <td style={styles.td}>
                    <div style={styles.rulesList}>
                      {run.rulesApplied?.map((rule, idx) => (
                        <span key={idx} style={styles.ruleChip}>{rule.name}</span>
                      ))}
                    </div>
                  </td>
                  <td style={styles.td}>{run.totalProcessed}</td>
                  <td style={styles.td}>{run.totalAssigned}</td>
                  <td style={styles.td}>{run.totalUnplaced}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        ...(STATUS_BADGE_STYLES[run.status] || STATUS_BADGE_STYLES.Pending),
                      }}
                    >
                      {run.status}
                    </span>
                  </td>
                </tr>

                {/* Expanded detail row */}
                {expandedRunId === run.id && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <div style={styles.detailPanel}>
                        {detailLoading && (
                          <div style={styles.loading}>Loading details...</div>
                        )}
                        {!detailLoading && runDetail && (
                          <>
                            {(run.status === 'Approved' || run.status === 'PartiallyApproved') && (
                              <div style={{ marginBottom: '0.75rem', textAlign: 'right' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReverseRun(run.id);
                                  }}
                                  disabled={reversing}
                                  style={{
                                    backgroundColor: '#d32f2f',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '0.4rem 0.85rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: reversing ? 'not-allowed' : 'pointer',
                                    opacity: reversing ? 0.6 : 1,
                                  }}
                                  aria-label={`Reverse run from ${formatDateTime(run.createdAt)}`}
                                >
                                  {reversing ? 'Reversing...' : '⟲ Reverse Run'}
                                </button>
                              </div>
                            )}
                            <RunDetailView detail={runDetail} />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RunDetailView({ detail }: { detail: RunDetail }) {
  // Group entries by table
  const entriesByTable = detail.entries.reduce<Record<number, typeof detail.entries>>((acc, entry) => {
    const tNum = entry.tableNumber;
    if (!acc[tNum]) acc[tNum] = [];
    acc[tNum].push(entry);
    return acc;
  }, {});

  const sortedTableNumbers = Object.keys(entriesByTable)
    .map(Number)
    .sort((a, b) => a - b);

  const entryStatusColors: Record<string, React.CSSProperties> = {
    Approved: { color: '#2e7d32' },
    Proposed: { color: '#546e7a' },
    Released: { color: '#d32f2f' },
  };

  return (
    <div>
      {sortedTableNumbers.map((tableNum) => {
        const entries = entriesByTable[tableNum];
        const zoneName = entries[0]?.table?.zone?.name || entries[0]?.zoneName || '';
        return (
          <div key={tableNum} style={styles.detailTableGroup}>
            <div style={styles.detailTableHeader}>
              <span>Table {tableNum}{zoneName ? ` — ${zoneName}` : ''}</span>
              <span style={{ fontSize: '0.68rem', color: TEXT_MUTED }}>{entries.length} guests</span>
            </div>
            {entries.map((entry) => (
              <div key={entry.id} style={styles.detailGuestRow}>
                <span style={{ flex: 1, fontWeight: 500 }}>{entry.guest.fullName}</span>
                <span style={{ width: '55px', color: TEXT_MUTED }}>Seat {entry.seatNumber}</span>
                {entry.ruleThatPlaced && (
                  <span style={{ fontSize: '0.65rem', color: '#6a1b9a', backgroundColor: '#f3e5f5', padding: '0.1rem 0.25rem', borderRadius: '3px' }}>
                    {entry.ruleThatPlaced}
                  </span>
                )}
                {entry.groupLabel && (
                  <span style={{ fontSize: '0.65rem', color: '#e65100', backgroundColor: '#fff3e0', padding: '0.1rem 0.25rem', borderRadius: '3px' }}>
                    {entry.groupLabel}
                  </span>
                )}
                <span style={{ fontSize: '0.65rem', fontWeight: 500, ...(entryStatusColors[entry.status] || {}) }}>
                  {entry.status}
                </span>
              </div>
            ))}
          </div>
        );
      })}

      {detail.entries.length === 0 && (
        <div style={{ padding: '1rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.8rem' }}>
          No entries in this run.
        </div>
      )}
    </div>
  );
}
